import {
  Injectable,
  computed,
  effect,
  inject,
  linkedSignal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  type CanActivateFn,
  type CanMatchFn,
  Router,
  UrlTree,
} from '@angular/router';
import type { OrgRole } from '@shared/models/auth';
import type { User } from '@shared/models/user';
import { WINDOW } from '@tvd/cdk/browser';
import { filter, first, map, of, switchMap } from 'rxjs';
import { BranchStore } from '../branch';
import { AuthUser } from './auth-user';

const PINNED_ORG_KEY = 'pinnedOrg';
const PINNED_BRANCH_KEY = 'pinnedBranch';

@Injectable({
  providedIn: 'root',
})
export class PinnedBranch {
  readonly #storage = inject(WINDOW).localStorage;
  readonly #user = inject(AuthUser);

  readonly #roles = computed(() => this.#user.data()?.roles || []);

  readonly #role = computed(() => {
    const orgId = this.#storage.getItem(PINNED_ORG_KEY);
    let role = this.#roles().find((role) => role.organisation === orgId);

    if (!role && !this.pinningAllowed()) {
      role = this.#roles()[0];
      if (!role) {
        return null;
      }
    }
    return role;
  });

  readonly org = linkedSignal(() => this.#role()?.organisation);

  readonly branch = linkedSignal(() => {
    if (!this.org()) {
      return undefined;
    }
    let branchId = this.#storage.getItem(PINNED_BRANCH_KEY);
    if (!branchId) {
      const role = this.#role();
      if (!role) {
        return undefined;
      }
      branchId = role.shop;
    }

    return branchId;
  });

  readonly pinningAllowed = computed(() => {
    const roles = this.#roles();
    return roles.reduce((acc, r) => acc + r.shops.length, 0) > 1;
  });

  isPinned(orgId: string, branchId: string) {
    return this.org() === orgId && this.branch() === branchId;
  }

  pin(orgId: string, branchId?: string) {
    this.org.set(orgId);
    this.branch.set(branchId);
  }

  unpin() {
    this.org.set(undefined);
    this.branch.set(undefined);
  }

  readonly #change = effect(() => {
    const orgId = this.org();
    const branchId = this.branch();

    if (orgId) {
      this.#storage.setItem(PINNED_ORG_KEY, orgId);
    } else {
    }

    if (branchId) {
      this.#storage.setItem(PINNED_BRANCH_KEY, branchId);
    } else {
    }
  });
}

export const redirectToOrg: CanMatchFn = (_route, segments) => {
  const router = inject(Router);
  const auth = inject(AuthUser);
  const pinnedBranch = inject(PinnedBranch);

  // If there are already segments (e.g., orgId), allow the route to match
  if (segments.length > 1) {
    return true;
  }

  const user$ = toObservable(auth.data);
  return user$.pipe(
    first((data): data is User => !!data),
    map((user) => {
      const roles = user.roles;

      if (roles.length === 0) {
        return router.createUrlTree(['/onboarding-wizard']);
      }

      let orgId = pinnedBranch.org();
      if (!roles.some((role) => role.organisation === orgId)) {
        orgId = roles[0].organisation;
      }
      return router.createUrlTree(['/dashboard', orgId]);
    }),
  );
};

export const redirectToBranch: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const auth = inject(AuthUser);
  const pinnedBranch = inject(PinnedBranch);
  const user$ = toObservable(auth.data);
  const branchStore = inject(BranchStore);

  return user$.pipe(
    first((data): data is User => !!data),
    map((user) => {
      const roles = user.roles;
      const orgId = route.paramMap.get('orgId');
      const role = roles.find((role) => role.organisation === orgId);
      if (!role) {
        return router.createUrlTree(['/onboarding-wizard']);
      }
      return role;
    }),
    filter((role): role is OrgRole => !(role instanceof UrlTree)),
    switchMap((role) => {
      if (role.shops.length === 0) {
        return branchStore
          .getBranches(role.organisation)
          .pipe(
            map((branches) => ({ ...role, shops: branches.map((b) => b.id) })),
          );
      }
      return of(role);
    }),
    map((role) => {
      let branchId = pinnedBranch.branch();

      if ((branchId && !role.shops.includes(branchId)) || !branchId) {
        branchId = role.shop || role.shops[0];
      }
      return router.createUrlTree(['/dashboard', role.organisation, branchId]);
    }),
  );
};
